import React from 'react';

import { FC, ReactNode, useEffect, useRef } from 'react';
import { IcoClose2 } from 'icons/indexed-icons/IcoClose';
import { FlyoutAndPopupsContainer } from './FlyoutsAndPopups.elements';
import { useTranslation } from 'react-i18next';
import useEscapeKey from 'common/hooks/useEscapeKey';

export interface FlyoutProps {
  Header?: JSX.Element;
  /** custom close component */
  CloseIcon?: JSX.Element;
  /** this renders in the middle area, can be sent as children too */
  Component?: FC<Record<string, unknown>>;
  Footer?: JSX.Element | undefined;
  type?: 'flyout' | 'modal' | 'full-screen';
  /** appears at the top of the popup */
  title?: ReactNode;
  TitleIcons?: JSX.Element | JSX.Element[];
  /** is the flyout open or closed, this must be controlled externally */
  isOpen?: boolean;
  /** this is require as this is a externally handled */
  handleClose?: (e?: HTMLElement) => void;
  children?: ReactNode;
  className?: string;
  popup?: boolean;
  /** loads the popup and is controled via classes and js externall */
  load?: boolean;
  /** use to populate data-id tag */
  id?: string;
}

export const Flyout = ({
  CloseIcon,
  Header,
  Component,
  type,
  title,
  Footer,
  isOpen,
  handleClose,
  children,
  className,
  popup,
  load,
  id
}: FlyoutProps): JSX.Element => {

  const containerRef = useRef(null);
  const { t } = useTranslation(['mh']);

  if (typeof title === 'string') title = t(title);

  // ref is used for useClickoutside
  const ref = useRef(null);
  // start the grid layout and build from there
  // we start with the close button/title row
  let gridRows = 'auto';
  // add header row if needded
  if (Header) gridRows += ' auto';
  // add main content row no matter what
  gridRows += ' 1fr';
  // add footer row if needed
  if (Footer) gridRows += ' auto';
  //default is flyout for now
  type = popup ? 'modal'  : type || 'flyout';

  className = `popup-container ${className || ''}`.trim();

  if (type) className += ` is-${type}`;
  if (isOpen) className += ' is-open';

  if (Header && Footer) className += ' has-header-footer';
  else {
    if (Header) className += ' has-header';
    if (Footer) className += ' has-footer';
  }

  const closeHandler = ( ) => {
    document.body.classList.remove('popup-open');
    if (handleClose) return handleClose?.(containerRef?.current || undefined)
    const container = containerRef?.current as HTMLElement | null;
    return container?.classList?.remove('is-open');
  };
 
  const MainToUse = (): JSX.Element => 
    Component ? <Component /> : children as JSX.Element;
  
  //useClickOutside(ref, () => ((isOpen || load) ? closeHandler() : null));
  useEscapeKey(() => ((isOpen || load) ? closeHandler() : null));
  useEffect(() => {
    if (load) return 
    return isOpen ? document.body.classList.add('popup-open') : document.body.classList.remove('popup-open')
    // if (isOpen) return document.body.classList.add('popup-open');
    // return () => document.body.classList.remove('popup-open');
  }, [isOpen]);

  return (
    <FlyoutAndPopupsContainer {...{ className, gridRows }}  ref={containerRef} data-id={id || undefined}>
      {
        // the wrapper is the white are and contains the drop shadow
        <div className="popup-wrapper" ref={ref}>
          {
            // the title bar is the top row with the title and close button
            <div className="popup-title-bar">
              <div className="popup-title-bar__header">
                {typeof title === 'string' ? <h2>{title}</h2> : title}
              </div>
              <button className="btn-close" type="button" onClick={() => closeHandler()}>
                {CloseIcon || <IcoClose2 />}
              </button>
            </div>
          }

          {
            // the header is the row below the title bar and is optional
            Header && <div className="popup-header">{Header}</div>
          }

          {
            // the middle scroll section of the popup of flyout1
          
              <div className="popup-main">
               <MainToUse />
              </div>
      
          }

          {
            // the fixed bottom of the popup or flyout
            Footer && <div className="popup-footer">{Footer}</div>
          }
        </div>
      }
      <span className='close-trigger' onClick={closeHandler}></span>
    </FlyoutAndPopupsContainer>
  );
};
